import {APIClient} from "../../utils/APIClient";
import {buildMultipartHeaders} from "../../utils/multipart";
import {API_URL} from "../../settings/local_settings";

// API de la burbuja de chat de soporte (Reemplazo de Intercom, Fase 3).
// Base: API_URL. Auth: header "Authorization: Token <token>" (igual que APIClient).
//
// Los GET/POST con JSON van por APIClient normal (hereda cache/errores/Sentry).
// createConversation/postMessage usan MULTIPART (message + attachment opcional): APIClient no
// soporta FormData, así que hacen fetch directo calcando los headers de APIClient (Token +
// X-Dashbo-Client) SIN setear Content-Type (el browser pone el boundary del multipart).
//
// Ninguna función tira por red: siempre devuelven un objeto (shape NETWORK_ERROR ante fallo),
// igual que APIClient, para que los callers puedan hacer `data.status` sin crashear.

const NETWORK_ERROR_RESPONSE = {
    status: "NETWORK_ERROR",
    error_code: "NETWORK_ERROR",
    error_message: "Se ha producido un error de conexión. Por favor, reintentá en unos segundos.",
};

const RATE_LIMITED_DEFAULT_MESSAGE = "Estás enviando mensajes muy rápido. Esperá unos segundos e intentá de nuevo.";

// ── Lecturas / acciones JSON (via APIClient) ──────────────────────────────────

export function fetchConversations() {
    return new APIClient(API_URL + 'support/conversations/').call('GET');
}

export function fetchMessages(conversationId, {after, beforeId, limit} = {}) {
    const params = new URLSearchParams();
    if (after) params.set('after', after);
    if (beforeId !== undefined && beforeId !== null) params.set('before_id', String(beforeId));
    if (limit !== undefined && limit !== null) params.set('limit', String(limit));
    const qs = params.toString();
    const url = `${API_URL}support/conversations/${conversationId}/messages/${qs ? '?' + qs : ''}`;
    return new APIClient(url).call('GET');
}

export function markRead(conversationId) {
    return new APIClient(`${API_URL}support/conversations/${conversationId}/mark-read/`).call('POST');
}

export function fetchUnreadCount() {
    return new APIClient(API_URL + 'support/unread-count/').call('GET');
}

// Mensajes del equipo que el usuario todavía no leyó (la tarjeta junto a la burbuja) + el contador
// del badge. Sólo trae los del DESTINATARIO: un colega de la agencia recibe la lista vacía.
export function fetchIncoming() {
    return new APIClient(API_URL + 'support/incoming/').call('GET');
}

// ── Escrituras MULTIPART (fetch directo) ──────────────────────────────────────

// Los headers de auth (Token + X-Dashbo-Client, SIN Content-Type: el browser pone el boundary)
// viven en `utils/multipart.js`, compartidos con el widget de feedback y la Memoria del cliente.

// attachmentBlob opcional: { blob, filename }.
function buildFormData(message, attachmentBlob) {
    const formData = new FormData();
    formData.append('message', message !== undefined && message !== null ? message : '');
    if (attachmentBlob && attachmentBlob.blob) {
        formData.append('attachment', attachmentBlob.blob, attachmentBlob.filename);
    }
    return formData;
}

// Interpreta una respuesta no-2xx del multipart al mismo shape que usa APIClient:
// - 429 (throttle DRF, body {detail}) → error_code 'RATE_LIMITED'.
// - resto: intenta parsear el JSON del body ({status, error_message}); si el body no es JSON
//   parseable, cae al shape NETWORK_ERROR (igual que APIClient ante un 500 con HTML).
async function handleMultipartError(response) {
    if (response.status === 429) {
        let detail = RATE_LIMITED_DEFAULT_MESSAGE;
        try {
            const body = await response.json();
            if (body && body.detail) detail = body.detail;
        } catch (_) {
            // body no-JSON: usamos el mensaje default.
        }
        return {
            status: "ERROR",
            error_code: "RATE_LIMITED",
            error_message: detail,
        };
    }

    try {
        const body = await response.json();
        return {
            status: body.status || "ERROR",
            error_message: body.detail || body.message || body.error_message || "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
            error_code: body.status || "ERROR",
        };
    } catch (_) {
        // Body no-JSON (ej. 500 con HTML): mismo fallback que APIClient.
        return {...NETWORK_ERROR_RESPONSE};
    }
}

async function postMultipart(url, message, attachmentBlob) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: buildMultipartHeaders(),
            body: buildFormData(message, attachmentBlob),
        });
        if (!response.ok) {
            return await handleMultipartError(response);
        }
        return await response.json();
    } catch (_) {
        // Fallo de red / body no-parseable de un 2xx: mismo shape que APIClient.
        return {...NETWORK_ERROR_RESPONSE};
    }
}

export function createConversation(message, attachmentBlob) {
    return postMultipart(API_URL + 'support/conversations/', message, attachmentBlob);
}

export function postMessage(conversationId, message, attachmentBlob) {
    return postMultipart(
        `${API_URL}support/conversations/${conversationId}/messages/`,
        message,
        attachmentBlob,
    );
}

export const SupportChatApi = {
    fetchConversations,
    fetchMessages,
    markRead,
    fetchUnreadCount,
    fetchIncoming,
    createConversation,
    postMessage,
};
